/**
 * The routes.
 *
 * Five of these are also SERVER routes: `/signin`, `/link/start`,
 * `/oauth/authorize`, `/account` and `/install` (which every invitation email
 * links to, and the download redirects back to) are all places the Worker
 * sends a browser mid-flow, and it serves this app for each of them (see src/index.ts and
 * wrangler.jsonc's `single-page-application` asset handling). Renaming one
 * here without renaming it there breaks a redirect, not a link.
 */

import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import Container from "react-bootstrap/Container";
import { BareLayout, Layout } from "./components/Layout";
import { SkeletonRows } from "./components/ui";
import { Account } from "./pages/Account";
import { Admin } from "./pages/Admin";
import { Authorize } from "./pages/Authorize";
import { Connect } from "./pages/Connect";
import { Install } from "./pages/Install";
import { Landing } from "./pages/Landing";
import { LinkBrowser } from "./pages/LinkBrowser";
import { NotFound } from "./pages/NotFound";
import { SignIn } from "./pages/SignIn";
import { useSession } from "./useSession";

/**
 * Gate for the pages that need an account. Sends the visitor to sign in and
 * back again rather than showing them an empty page with a 401 in it.
 */
function RequireAccount({ children }: { children: ReactNode }): ReactNode {
  const { account, loading } = useSession();
  const location = useLocation();
  if (loading) {
    return (
      <Container style={{ maxWidth: "60rem" }}>
        <SkeletonRows rows={6} />
      </Container>
    );
  }
  if (!account) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/signin?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}

export function App(): ReactNode {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/connect" element={<Connect />} />
        <Route
          path="/account"
          element={
            <RequireAccount>
              <Account />
            </RequireAccount>
          }
        />
        <Route
          path="/install"
          element={
            <RequireAccount>
              <Install />
            </RequireAccount>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAccount>
              <Admin />
            </RequireAccount>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route element={<BareLayout />}>
        {/* The Worker serves the app at /link/start; /link is the same page for
            anyone who shortens the URL by hand. */}
        <Route path="/link/start" element={<LinkBrowser />} />
        <Route path="/link" element={<LinkBrowser />} />
        <Route path="/oauth/authorize" element={<Authorize />} />
      </Route>
    </Routes>
  );
}
